/**
 * PermissionOverridesModal Component
 * Allows administrators to configure granular permission overrides beyond
 * the user's primary base role.
 */

import { useState, useEffect } from 'react'
import {
  Shield,
  Search,
  Check,
  Ban,
  RotateCcw,
  Sliders,
  X,
  AlertCircle,
} from 'lucide-react'
import type { User, UserPermissionOverrides } from '@/types/user.types'
import { getRoleDisplayName } from '@/types/auth.types'

interface PermissionItem {
  key: string
  label: string
  description: string
  category: string
}

const GRANULAR_PERMISSIONS: PermissionItem[] = [
  // Clients
  {
    key: 'txh.client.create',
    label: 'Create Clients',
    description: 'Onboard and create new client profiles',
    category: 'Clients',
  },
  {
    key: 'txh.client.read',
    label: 'View Clients',
    description: 'Browse and view client details and portfolios',
    category: 'Clients',
  },
  {
    key: 'txh.client.update',
    label: 'Edit Clients',
    description: 'Update client profile information and contacts',
    category: 'Clients',
  },
  {
    key: 'txh.client.delete',
    label: 'Delete Clients',
    description: 'Archive or remove client profiles',
    category: 'Clients',
  },
  {
    key: 'txh.client.export',
    label: 'Export Clients',
    description: 'Export client lists and data to CSV / Excel',
    category: 'Clients',
  },
  {
    key: 'txh.client.lookup_pan',
    label: 'Lookup PAN',
    description: 'Query government databases for PAN verification',
    category: 'Clients',
  },
  {
    key: 'txh.client.lookup_gstin',
    label: 'Lookup GSTIN',
    description: 'Query GST portal for GSTIN taxpayer validation',
    category: 'Clients',
  },
  {
    key: 'txh.client.note.create',
    label: 'Create Notes',
    description: 'Add client notes, comments, and reminders',
    category: 'Clients',
  },
  {
    key: 'txh.client.note.delete',
    label: 'Delete Notes',
    description: 'Remove internal client notes',
    category: 'Clients',
  },

  // Documents
  {
    key: 'txh.document.upload',
    label: 'Upload Documents',
    description: 'Upload client tax files and supporting materials',
    category: 'Documents',
  },
  {
    key: 'txh.document.read',
    label: 'View Documents',
    description: 'Access and preview client document files',
    category: 'Documents',
  },
  {
    key: 'txh.document.download',
    label: 'Download Documents',
    description: 'Download original client documents and attachments',
    category: 'Documents',
  },
  {
    key: 'txh.document.verify',
    label: 'Verify Documents',
    description: 'Mark uploaded client documents as verified or rejected',
    category: 'Documents',
  },
  {
    key: 'txh.document.delete',
    label: 'Delete Documents',
    description: 'Permanently remove or archive documents',
    category: 'Documents',
  },
  {
    key: 'txh.document.version.read',
    label: 'View Document Versions',
    description: 'Inspect revision history and prior file versions',
    category: 'Documents',
  },

  // Assessments
  {
    key: 'txh.assessment.create',
    label: 'Create Assessments',
    description: 'Initiate new income tax or corporate tax assessments',
    category: 'Assessments',
  },
  {
    key: 'txh.assessment.read',
    label: 'View Assessments',
    description: 'Review filings, computations, and assessment status',
    category: 'Assessments',
  },
  {
    key: 'txh.assessment.update',
    label: 'Update Assessments',
    description: 'Edit tax computations, advance tax, and TDS details',
    category: 'Assessments',
  },
  {
    key: 'txh.assessment.file',
    label: 'Record Filing & Ack',
    description: 'Record official portal filing acknowledgement numbers',
    category: 'Assessments',
  },
  {
    key: 'txh.assessment.delete',
    label: 'Delete Assessments',
    description: 'Delete or cancel drafted assessments',
    category: 'Assessments',
  },
  {
    key: 'txh.gst_assessment.read',
    label: 'View GST Returns',
    description: 'Inspect GSTR-1, GSTR-3B, and annual returns',
    category: 'Assessments',
  },
  {
    key: 'txh.gst_assessment.update',
    label: 'Update GST Returns',
    description: 'Edit GST return values and reconciliation summaries',
    category: 'Assessments',
  },

  // Reminders & Compliance
  {
    key: 'txh.reminder.create',
    label: 'Create Reminders',
    description: 'Set up statutory deadline and client reminder alerts',
    category: 'Reminders',
  },
  {
    key: 'txh.reminder.read',
    label: 'View Reminders',
    description: 'View compliance calendar and pending reminders',
    category: 'Reminders',
  },
  {
    key: 'txh.reminder.update',
    label: 'Update Reminders',
    description: 'Reschedule or reassign compliance reminders',
    category: 'Reminders',
  },
  {
    key: 'txh.reminder.delete',
    label: 'Delete Reminders',
    description: 'Remove scheduled reminders or task notifications',
    category: 'Reminders',
  },

  // Payments & Billing
  {
    key: 'txh.payment.create',
    label: 'Create Invoices',
    description: 'Generate professional fee invoices and fee requests',
    category: 'Billing',
  },
  {
    key: 'txh.payment.read',
    label: 'View Invoices & Payments',
    description: 'Browse client billing history and payment records',
    category: 'Billing',
  },
  {
    key: 'txh.payment.refund',
    label: 'Process Refunds',
    description: 'Authorize client refunds and fee adjustments',
    category: 'Billing',
  },

  // Audit & Governance
  {
    key: 'txh.audit.view',
    label: 'View Audit Trail',
    description: 'Access compliance audit logs and activity history',
    category: 'Audit & Settings',
  },
  {
    key: 'txh.deletion.read',
    label: 'View Recycle Bin',
    description: 'Inspect soft-deleted records and retention schedules',
    category: 'Audit & Settings',
  },
  {
    key: 'txh.deletion.restore',
    label: 'Restore Deleted Records',
    description: 'Restore items from the firm-wide recycle bin',
    category: 'Audit & Settings',
  },
  {
    key: 'txh.user.read',
    label: 'View Team Users',
    description: 'Inspect team member profiles and user directory',
    category: 'Audit & Settings',
  },
  {
    key: 'txh.user.update',
    label: 'Manage Team Users',
    description: 'Update user profiles, roles, and status',
    category: 'Audit & Settings',
  },
  {
    key: 'txh.settings.read',
    label: 'View Settings',
    description: 'Review organization and security policies',
    category: 'Audit & Settings',
  },
]

interface PermissionOverridesModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (overrides: UserPermissionOverrides) => Promise<void>
  user: User | null
}

export function PermissionOverridesModal({
  isOpen,
  onClose,
  onSave,
  user,
}: PermissionOverridesModalProps) {
  const [granted, setGranted] = useState<string[]>([])
  const [revoked, setRevoked] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize overrides from user preferences
  useEffect(() => {
    if (user?.preferences?.permissionOverrides) {
      setGranted(user.preferences.permissionOverrides.granted ?? [])
      setRevoked(user.preferences.permissionOverrides.revoked ?? [])
    } else {
      setGranted([])
      setRevoked([])
    }
  }, [user])

  if (!isOpen || !user) return null

  // Categories list
  const categories = ['All', 'Clients', 'Documents', 'Assessments', 'Reminders', 'Billing', 'Audit & Settings']

  // Filtered permissions
  const filteredPermissions = GRANULAR_PERMISSIONS.filter((p) => {
    if (selectedCategory !== 'All' && p.category !== selectedCategory) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return (
        p.key.toLowerCase().includes(q) ||
        p.label.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      )
    }
    return true
  })

  // Handlers for permission states
  const handleSetDefault = (key: string) => {
    setGranted((prev) => prev.filter((k) => k !== key))
    setRevoked((prev) => prev.filter((k) => k !== key))
  }

  const handleSetGranted = (key: string) => {
    setRevoked((prev) => prev.filter((k) => k !== key))
    setGranted((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }

  const handleSetRevoked = (key: string) => {
    setGranted((prev) => prev.filter((k) => k !== key))
    setRevoked((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }

  const handleResetAll = () => {
    setGranted([])
    setRevoked([])
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await onSave({
        granted,
        revoked,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save permission overrides.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const overrideCount = granted.length + revoked.length

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="permission-overrides-modal-title"
    >
      <div className="relative w-full max-w-3xl bg-surface-light dark:bg-surface-dark rounded-2xl shadow-2xl border border-surface-overlay overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <Sliders className="w-5 h-5" />
            </div>
            <div>
              <h2
                id="permission-overrides-modal-title"
                className="text-base font-semibold text-text-primary flex items-center gap-2"
              >
                Permission Scoping & Overrides
                {overrideCount > 0 && (
                  <span className="badge badge-primary badge-xs">
                    {overrideCount} active override{overrideCount !== 1 ? 's' : ''}
                  </span>
                )}
              </h2>
              <p className="text-xs text-text-tertiary">
                Customize access beyond base role for {user.fullName} ({getRoleDisplayName(user.role)})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-overlay transition-colors disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User context banner */}
        <div className="px-5 py-3 bg-surface-overlay/30 border-b border-surface-overlay flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary-500" />
            <span className="text-text-secondary">Base Role:</span>
            <span className="font-semibold text-text-primary">
              {getRoleDisplayName(user.role)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleResetAll}
              disabled={overrideCount === 0 || isSubmitting}
              className="btn btn-ghost btn-xs gap-1 text-xs text-text-tertiary hover:text-text-primary disabled:opacity-40"
            >
              <RotateCcw className="w-3 h-3" />
              Reset to Role Defaults
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b border-surface-overlay space-y-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search permissions by name, key, or description..."
                className="input input-bordered input-sm w-full pl-9 text-xs"
                aria-label="Search permissions"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-2.5 py-1 rounded-lg transition-colors whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-primary-500 text-white font-medium'
                    : 'bg-surface-overlay text-text-secondary hover:text-text-primary'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Body / Permissions List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-start gap-2 mb-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {filteredPermissions.length === 0 ? (
            <div className="p-8 text-center text-xs text-text-tertiary">
              No permissions found matching "{searchQuery}".
            </div>
          ) : (
            <div className="divide-y divide-surface-overlay">
              {filteredPermissions.map((perm) => {
                const isGranted = granted.includes(perm.key)
                const isRevoked = revoked.includes(perm.key)
                const isDefault = !isGranted && !isRevoked

                return (
                  <div
                    key={perm.key}
                    className="py-3 px-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-surface-overlay/30 rounded-xl transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-semibold text-text-primary">
                          {perm.label}
                        </p>
                        <code className="text-[10px] text-text-tertiary bg-surface-overlay px-1.5 py-0.5 rounded">
                          {perm.key}
                        </code>
                        {isGranted && (
                          <span className="badge badge-success badge-xs font-semibold">
                            + Explicitly Granted
                          </span>
                        )}
                        {isRevoked && (
                          <span className="badge badge-error badge-xs font-semibold">
                            - Explicitly Revoked
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-secondary mt-0.5">
                        {perm.description}
                      </p>
                    </div>

                    {/* Action Selector */}
                    <div className="flex items-center gap-1 bg-surface-overlay/60 p-1 rounded-lg self-start sm:self-center">
                      <button
                        type="button"
                        onClick={() => handleSetDefault(perm.key)}
                        className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                          isDefault
                            ? 'bg-white dark:bg-surface-dark text-text-primary shadow-xs'
                            : 'text-text-tertiary hover:text-text-primary'
                        }`}
                        title="Follow role default"
                      >
                        Role Default
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetGranted(perm.key)}
                        className={`px-2 py-1 rounded text-[11px] font-medium transition-colors flex items-center gap-1 ${
                          isGranted
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'text-text-tertiary hover:text-emerald-600'
                        }`}
                        title="Grant permission to user"
                      >
                        <Check className="w-3 h-3" />
                        Grant
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetRevoked(perm.key)}
                        className={`px-2 py-1 rounded text-[11px] font-medium transition-colors flex items-center gap-1 ${
                          isRevoked
                            ? 'bg-red-600 text-white shadow-xs'
                            : 'text-text-tertiary hover:text-red-600'
                        }`}
                        title="Revoke permission from user"
                      >
                        <Ban className="w-3 h-3" />
                        Revoke
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 bg-surface-overlay/20 border-t border-surface-overlay">
          <div className="text-xs text-text-tertiary">
            {granted.length} granted · {revoked.length} revoked
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-ghost btn-sm text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="btn btn-primary btn-sm text-xs gap-1.5"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              Save Permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PermissionOverridesModal
